// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { ReactNode } from 'react';

import type { Lens } from '../../bygning/selection';
import { SegmentedControl } from '../SegmentedControl';
import { useSettings } from './settings/SettingsContext';
import { labelNoteFor, labelSourcesFor, pickPrimary } from './settings/settingsState';

/** The v8 prototype's lens icons (24px grid, stroked). */
const ICON: Record<Lens, ReactNode> = {
  mat: (
    <>
      <rect x="3" y="3" width="7" height="7" rx="1.5" />
      <rect x="14" y="3" width="7" height="7" rx="1.5" />
      <rect x="3" y="14" width="7" height="7" rx="1.5" />
      <rect x="14" y="14" width="7" height="7" rx="1.5" />
    </>
  ),
  hier: <path d="M9 5h6M6 10h12M4 15h16M3 20h18" />,
  rev: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="m8.5 12 2.5 2.5 5-5" />
    </>
  ),
};

const LABEL: Record<Lens, string> = {
  mat: 'Farv efter materiale',
  hier: 'Farv efter affaldshierarki',
  rev: 'Farv efter status',
};

const LENSES: readonly Lens[] = ['mat', 'hier', 'rev'];

/** Etiket: colour by a label cloud (planes, rooms, instances …); a settings colouring, not a `?lens=`. */
const ETIKET = 'etiket';
type LensChoice = Lens | typeof ETIKET;

const TAG_ICON = (
  <>
    <path d="M3 12.2V4a1 1 0 0 1 1-1h8.2a1 1 0 0 1 .7.3l7.8 7.8a1 1 0 0 1 0 1.4l-7.2 7.2a1 1 0 0 1-1.4 0L3.3 12.9a1 1 0 0 1-.3-.7Z" />
    <circle cx="8" cy="8" r="1.3" />
  </>
);

/** A lens's icon; sized by its container (the segment's icon slot). */
export function LensIcon({ lens }: { lens: LensChoice }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {lens === ETIKET ? TAG_ICON : ICON[lens]}
    </svg>
  );
}

const ITEMS = LENSES.map((l) => ({ value: l as LensChoice, label: LABEL[l], icon: <LensIcon lens={l} /> }));

/**
 * Materiale · hierarki · status · etiket — what colours the building
 * (Ressourcer only). Etiket is the point cloud's label colouring
 * (Modelindstillinger › Farver), so it lives in the settings, not in `?lens=`;
 * picking a lens turns it off again.
 */
export function LensSwitch({ lens, onChange }: { lens: Lens; onChange: (lens: Lens) => void }) {
  const { state, dispatch, facts } = useSettings();
  const primary = pickPrimary(facts.clouds);
  const available = labelSourcesFor(facts.clouds, primary).length > 0;
  const items = [
    ...ITEMS,
    {
      value: ETIKET as LensChoice,
      label: 'Farv efter etiketsky',
      icon: <LensIcon lens={ETIKET} />,
      disabled: !available,
      title: available ? 'Farv efter etiketsky (planer, rum, instanser …)' : (labelNoteFor(facts.clouds) ?? undefined),
    },
  ];
  const value: LensChoice = state.colour === 'label' && available ? ETIKET : lens;
  const choose = (next: LensChoice) => {
    if (next === ETIKET) {
      dispatch({ type: 'set', patch: { colour: 'label' } });
      return;
    }
    if (state.colour === 'label') dispatch({ type: 'set', patch: { colour: 'lens' } });
    onChange(next);
  };
  return <SegmentedControl role="group" aria-label="Linse" iconOnly items={items} value={value} onChange={choose} />;
}
